import { Editor, Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

export const MIN_PARAGRAPH_INDENT = 0;
export const MAX_PARAGRAPH_INDENT = 8;
// Keep in sync with the `[data-indent]` CSS rules in rich-editor.component.html.
export const PARAGRAPH_INDENT_EM_PER_LEVEL = 2;

export function clampIndent(value: number): number {
  return Math.min(MAX_PARAGRAPH_INDENT, Math.max(MIN_PARAGRAPH_INDENT, Math.round(value) || 0));
}

export interface ParagraphIndentOptions {
  types: string[];
}

export const ParagraphIndent = Extension.create<ParagraphIndentOptions>({
  name: 'paragraphIndent',

  addOptions() {
    return { types: ['paragraph'] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element: HTMLElement) => {
              const raw = element.getAttribute('data-indent');
              const parsed = raw ? Number.parseInt(raw, 10) : 0;
              return Number.isFinite(parsed) ? clampIndent(parsed) : 0;
            },
            renderHTML: (attributes: Record<string, unknown>) => {
              const level = clampIndent(Number(attributes['indent']) || 0);
              if (level <= 0) {
                return {};
              }
              return {
                'data-indent': String(level),
                style: `margin-left: ${level * PARAGRAPH_INDENT_EM_PER_LEVEL}em`,
              };
            },
          },
        },
      },
    ];
  },
});

/**
 * Computes the {pos, nextIndent} updates for every paragraph intersecting
 * [from, to], skipping paragraphs already clamped at the min/max so they're
 * left untouched.
 */
export function computeIndentUpdates(
  doc: ProseMirrorNode,
  from: number,
  to: number,
  delta: number,
): Array<{ pos: number; nextIndent: number }> {
  const updates: Array<{ pos: number; nextIndent: number }> = [];
  const seen = new Set<number>();

  const visit = (node: ProseMirrorNode, pos: number) => {
    if (node.type.name !== 'paragraph' || seen.has(pos)) {
      return;
    }
    seen.add(pos);
    const current = clampIndent(Number(node.attrs['indent']) || 0);
    const next = clampIndent(current + delta);
    if (next !== current) {
      updates.push({ pos, nextIndent: next });
    }
  };

  doc.nodesBetween(from, to, visit);

  if (updates.length === 0 && !seen.size) {
    const $from = doc.resolve(from);
    if ($from.parent.type.name === 'paragraph') {
      visit($from.parent, $from.before($from.depth));
    }
  }

  return updates;
}

/**
 * Bumps/clamps the indent level of every paragraph intersecting the current
 * selection in a single transaction. Returns false if no paragraph needed
 * to change (e.g. all already at the min/max, or selection isn't in a
 * paragraph at all).
 */
export function changeParagraphIndent(editor: Editor, delta: 1 | -1): boolean {
  const { from, to } = editor.state.selection;
  const updates = computeIndentUpdates(editor.state.doc, from, to, delta);
  if (updates.length === 0) {
    return false;
  }

  return editor
    .chain()
    .focus()
    .command(({ tr }) => {
      updates.forEach(({ pos, nextIndent }) => {
        const node = tr.doc.nodeAt(pos);
        if (!node || node.type.name !== 'paragraph') {
          return;
        }
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: nextIndent });
      });
      return true;
    })
    .run();
}

/**
 * Shared dispatch used by both the toolbar Increase/Decrease Indent buttons
 * and the global Tab/Shift-Tab keyboard shortcuts: sink/lift the current
 * list item first, falling back to structural paragraph indent.
 */
export function indentAtCursor(editor: Editor): boolean {
  if (editor.isActive('taskItem')) {
    return editor.chain().focus().sinkListItem('taskItem').run();
  }

  const didSink = editor.chain().focus().sinkListItem('listItem').run();
  if (didSink) {
    return true;
  }

  return changeParagraphIndent(editor, 1);
}

export function outdentAtCursor(editor: Editor): boolean {
  if (editor.isActive('taskItem')) {
    return editor.chain().focus().liftListItem('taskItem').run();
  }

  const didLift = editor.chain().focus().liftListItem('listItem').run();
  if (didLift) {
    return true;
  }

  return changeParagraphIndent(editor, -1);
}
